/**
 * Environment variable types and validation
 */

export interface AppConfig {
  /** API base URL for backend communication */
  API_URL: string
  /** WebSocket URL for real-time messaging */
  WS_URL: string
  /** App name for branding */
  APP_NAME: string
  /** App version for tracking */
  APP_VERSION: string
  /** Current environment (development, production, etc.) */
  ENVIRONMENT: string
  /** Whether debug mode is enabled */
  DEBUG: boolean
  /** Log level for debugging */
  LOG_LEVEL: string
  /** Whether analytics are enabled */
  ENABLE_ANALYTICS: boolean
  /** Whether crash reporting is enabled */
  ENABLE_CRASH_REPORTING: boolean
}

/**
 * Get and validate environment configuration
 * @returns Validated app configuration object
 * @throws Error if required environment variables are missing
 */
export function getAppConfig(): AppConfig {
  const env = import.meta.env as any // Vite env types

  // Required environment variables
  const API_URL = env.EXPO_PUBLIC_API_URL || 'http://localhost:8000/api'

  // WebSocket URL: explicit env var OR derive from API_URL
  let WS_URL: string
  if (env.EXPO_PUBLIC_WS_URL) {
    WS_URL = env.EXPO_PUBLIC_WS_URL
  } else {
    // Derive from API_URL: https://api.masonyt.com/api → wss://api.masonyt.com/ws/messages/
    const url = new URL(API_URL)
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    WS_URL = `${wsProtocol}//${url.host}/ws/messages/`
  }

  // Optional environment variables with defaults
  const APP_NAME = env.EXPO_PUBLIC_APP_NAME || 'Masonyt'
  const APP_VERSION = env.EXPO_PUBLIC_APP_VERSION || '0.1.0'
  const ENVIRONMENT = env.EXPO_PUBLIC_ENVIRONMENT || 'development'
  const DEBUG = env.EXPO_PUBLIC_DEBUG === 'true' || env.NODE_ENV === 'development'
  const LOG_LEVEL = env.EXPO_PUBLIC_LOG_LEVEL || 'info'
  const ENABLE_ANALYTICS = env.EXPO_PUBLIC_ENABLE_ANALYTICS === 'true'
  const ENABLE_CRASH_REPORTING = env.EXPO_PUBLIC_ENABLE_CRASH_REPORTING === 'true'

  return {
    API_URL,
    WS_URL,
    APP_NAME,
    APP_VERSION,
    ENVIRONMENT,
    DEBUG,
    LOG_LEVEL,
    ENABLE_ANALYTICS,
    ENABLE_CRASH_REPORTING,
  }
}

/**
 * Validated app configuration singleton
 */
export const appConfig = getAppConfig()
